import React, { type MouseEventHandler, type ReactNode, type Ref, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, LinkProps } from 'react-router-dom';

import { LoadingButton } from '@mui/lab';
import { CircularProgress, ButtonProps as MaterialButtonProps } from '@mui/material';
import { Button as DsButton, ButtonLink as DsButtonLink, TextLink as DsTextLink, cn } from '@natter-co/design-system';
import { VISUAL_REFRESH } from 'common/utils';

import { toDsProps, toTextLinkProps } from './toDsProps';
import { mapButtonSizeToLoaderSize } from './utils/mapButtonSizeToLoaderSize';

export type ButtonProps = Omit<
  Pick<
    MaterialButtonProps,
    | 'children'
    | 'className'
    | 'disabled'
    | 'fullWidth'
    | 'href'
    | 'size'
    | 'startIcon'
    | 'endIcon'
    | 'sx'
    | 'type'
    | 'variant'
    | 'tabIndex'
    | 'onClick'
    | 'ref'
    | 'form'
    | 'disableRipple'
  >,
  'ref'
> & {
  color?: 'primary' | 'secondary' | 'info' | 'error' | 'inherit';
  externalHref?: string;
  isLoading?: boolean;
  loadingIndicator?: ReactNode;
};

// This is necessary because otherwise we get a console error from MUI as it uses ref
const LinkComponent = React.forwardRef<HTMLAnchorElement, LinkProps>(({ to, ...props }, ref) => (
  <Link innerRef={ref} to={to} {...props} />
));

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      externalHref,
      href,
      isLoading = false,
      loadingIndicator,
      size = 'medium',
      variant = 'contained',
      ...props
    },
    ref
  ) => {
    // `text-no-padding` is drawn as a standalone `TextLink` (see `toTextLinkProps`); everything else is the
    // DS button below.
    const { t } = useTranslation('common');
    const textLinkProps = VISUAL_REFRESH
      ? toTextLinkProps({ children, externalHref, href, isLoading, loadingIndicator, size, variant, ...props })
      : null;

    if (textLinkProps) {
      // Same consumed-or-meaningless set as the DS branch below; `type` and `form` go too, because the
      // element is an anchor or a bare `button` we type ourselves.
      const {
        color: _color,
        disableRipple: _disableRipple,
        disabled: _disabled,
        form: _form,
        fullWidth: _fullWidth,
        sx: _sx,
        type: _type,
        ...rest
      } = props;
      // The legacy text button was natter-text-sm, 14px / 500 (12px when `size="small"`). A text link inherits
      // its type from the line around it, so the wrapper restates the old one rather than let every call
      // site's container decide.
      const linkProps = {
        standalone: true as const,
        appearance: textLinkProps.appearance,
        ...rest,
        className: cn(size === 'small' ? 'text-label-md' : 'text-label-lg', rest.className),
      };
      const elementRef = ref as unknown as Ref<HTMLElement>;
      const handleClick = props.onClick as MouseEventHandler<HTMLElement> | undefined;

      if (href) {
        return (
          <DsTextLink {...linkProps} ref={elementRef} onClick={handleClick} render=<Link to={href} />>
            {children}
          </DsTextLink>
        );
      }

      if (externalHref) {
        return (
          <DsTextLink
            {...linkProps}
            ref={elementRef}
            onClick={handleClick}
            href={externalHref}
            external
            externalLabel={t('opensInNewTab')}
          >
            {children}
          </DsTextLink>
        );
      }

      // `render` target, not a hand-rolled button: `TextLink standalone` is the DS component, and a `button`
      // element is how it acts rather than navigates.
      // oxlint-disable-next-line react/forbid-elements
      const buttonElement = <button type="button" />;

      return (
        <DsTextLink {...linkProps} ref={elementRef} onClick={handleClick} render={buttonElement}>
          {children}
        </DsTextLink>
      );
    }

    /**
     * The visual-refresh gate lives HERE, not at the call sites — all 244 of them keep this
     * component's MUI-shaped API and never mention the flag. `toDsProps` returns `null` for anything
     * DS 2.0 cannot express, which falls through to the MUI implementation below.
     *
     * Links keep their element. MUI already renders an anchor for `href` (via `LinkComponent` + `to`)
     * and for `externalHref`, so swapping in `ButtonLink` changes the class list and nothing else —
     * same react-router navigation, same cmd-click and middle-click, same `link` role.
     */
    const dsProps = VISUAL_REFRESH
      ? toDsProps({ children, externalHref, href, isLoading, loadingIndicator, size, variant, ...props })
      : null;

    if (dsProps) {
      // Consumed or refused by `toDsProps`, and meaningless on a component with no Emotion and no
      // ripple — so they must not be forwarded. Everything else (`data-testid`, `onClick`,
      // `fullWidth`, icons, `tabIndex`) passes straight through.
      const { color: _color, disableRipple: _disableRipple, sx: _sx, ...rest } = props;

      if (href || externalHref) {
        // An anchor accepts none of these. `toDsProps` refuses a disabled link outright, so dropping
        // `disabled` here cannot lose behaviour.
        const { disabled: _disabled, form: _form, onClick, type: _type, ...linkProps } = rest;

        // `ButtonProps` inherits MUI's button-shaped handler, but the element under these branches
        // is — and already was, under MUI — an anchor. The cast corrects the element type rather
        // than changing any behaviour; `MouseEventHandler` is invariant in it, so it cannot be
        // widened away.
        const handleClick = onClick as MouseEventHandler<HTMLAnchorElement> | undefined;

        // Same correction, same reason: `forwardRef<HTMLButtonElement>` describes the common case,
        // but these two branches render an anchor. The legacy path below forwards the ref to that
        // anchor (via `loadingButtonProps`), so omitting it here would make the ref arrive under
        // one flag state and vanish under the other — silently, with no type error, since `sx` is
        // not the only prop a Tailwind component can swallow. `Ref` is invariant in its element, so
        // it needs the double cast.
        const anchorRef = ref as unknown as Ref<HTMLAnchorElement>;

        // `ButtonLink` has no busy state, so `loading` is not one of its props and spreading it puts
        // a literal `loading="false"` on the anchor — an invalid DOM attribute React warns about.
        // Always exactly `false` here rather than sometimes `true`, because `toDsProps` already
        // refuses a busy link outright, so nothing is lost by dropping it.
        const { loading: _loading, ...dsLinkProps } = dsProps;

        return href ? (
          <DsButtonLink {...linkProps} {...dsLinkProps} ref={anchorRef} onClick={handleClick} render=<Link to={href} />>
            {children}
          </DsButtonLink>
        ) : (
          <DsButtonLink
            {...linkProps}
            {...dsLinkProps}
            ref={anchorRef}
            href={externalHref}
            onClick={handleClick}
            rel="noopener noreferrer"
            target="_blank"
          >
            {children}
          </DsButtonLink>
        );
      }

      return (
        <DsButton ref={ref} {...rest} {...dsProps}>
          {children}
        </DsButton>
      );
    }

    const loadingButtonProps = {
      ref,
      href: externalHref,
      loadingIndicator: loadingIndicator || <CircularProgress color="inherit" size={mapButtonSizeToLoaderSize(size)} />,
      LinkComponent: href ? LinkComponent : undefined,
      loading: isLoading,
      size,
      target: externalHref ? '_blank' : undefined,
      // Same guard as the DS branch above: a new tab must not get `window.opener`.
      rel: externalHref ? 'noopener noreferrer' : undefined,
      to: href,
      variant,
      ...props,
    };

    return (
      <LoadingButton {...loadingButtonProps}>
        {/**
         * This is required because MUI renders the children even when loading
         * and this way the width of the button does not match the designs
         */}
        {!isLoading ? children : null}
      </LoadingButton>
    );
  }
);
